import { useEffect, useRef } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import {
  ORDER_ASSIGNED_BROADCAST_EVENT,
  ORDER_ASSIGNMENT_REALTIME_CHANNEL,
} from '@/shared/types/fulfillmentEvents.types';
import { getSupabaseRealtimeClient } from '../services/supabaseRealtimeClient';

export interface OrderAssignedRealtimeEvent {
  readonly orderId: string;
  readonly userTagId: number | null;
}

interface UseOrderAssignmentRealtimeParams {
  readonly enabled?: boolean;
  readonly onAssigned: (event: OrderAssignedRealtimeEvent) => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readUserTagId(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function parseOrderAssignedBroadcastPayload(raw: unknown): OrderAssignedRealtimeEvent | null {
  const body = isRecord(raw) && isRecord(raw.payload) ? raw.payload : raw;
  if (!isRecord(body)) {
    return null;
  }
  const orderId = body.orderId;
  if (typeof orderId !== 'string' || orderId.length === 0) {
    return null;
  }
  return {
    orderId,
    userTagId: readUserTagId(body.userTagId),
  };
}

export function useOrderAssignmentRealtime({
  enabled = true,
  onAssigned,
}: UseOrderAssignmentRealtimeParams): void {
  const onAssignedRef = useRef(onAssigned);

  useEffect(() => {
    onAssignedRef.current = onAssigned;
  }, [onAssigned]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const realtimeClient = getSupabaseRealtimeClient();
    if (!realtimeClient) {
      return undefined;
    }

    const channel: RealtimeChannel = realtimeClient.channel(ORDER_ASSIGNMENT_REALTIME_CHANNEL, {
      config: { broadcast: { self: true } },
    });

    channel.on('broadcast', { event: ORDER_ASSIGNED_BROADCAST_EVENT }, (message: unknown) => {
      const parsed = parseOrderAssignedBroadcastPayload(message);
      if (parsed == null) {
        return;
      }
      onAssignedRef.current(parsed);
    });

    channel.subscribe((status: string) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.warn('[order-assignment] Realtime channel error:', status);
      }
    });

    return () => {
      realtimeClient.removeChannel(channel).catch(() => {
        // no-op cleanup
      });
    };
  }, [enabled]);
}
